import Keycloak from "keycloak-js";
import { KEYCLOAK_CLIENT_ID, KEYCLOAK_REALM, KEYCLOAK_URL } from "../config";

// Bumped on every diagnostic build so we can confirm in DevTools console
// which bundle the browser actually loaded.
const BUILD_TAG = "fairregister-frontend build v7 (sub fallback + minio CORS)";

// One-shot banner at module load.
console.info(
  "%c" + BUILD_TAG,
  "background:#003;color:#fff;padding:2px 6px;border-radius:3px",
  { KEYCLOAK_URL, KEYCLOAK_REALM, KEYCLOAK_CLIENT_ID }
);

const log = (...args) =>
  console.info("%c[fairregister/auth]", "color:#0a7", ...args);
const warn = (...args) =>
  console.warn("%c[fairregister/auth]", "color:#a70", ...args);

const _kc = new Keycloak({
  url: KEYCLOAK_URL,
  clientId: KEYCLOAK_CLIENT_ID,
  realm: KEYCLOAK_REALM,
  "ssl-requiered": "external",
  "public-client": true,
});

_kc.onAuthSuccess = () => log("event: authenticated", {
  sub: _kc.tokenParsed?.sub ?? _kc.idTokenParsed?.sub,
  username:
    _kc.tokenParsed?.preferred_username ??
    _kc.idTokenParsed?.preferred_username,
  expiresIn: _kc.tokenParsed?.exp
    ? _kc.tokenParsed.exp - Math.floor(Date.now() / 1000) + "s"
    : "?",
});
_kc.onAuthError = (err) => warn("event: auth error", err);
_kc.onAuthLogout = () => log("event: logout");
_kc.onAuthRefreshSuccess = () => log("event: token refreshed");
_kc.onAuthRefreshError = () => warn("event: token refresh failed");
_kc.onTokenExpired = () => log("event: access token expired");

/**
 * Initializes Keycloak and renders the React app via the callback either
 * way — so the user always sees the UI, even if the silent SSO check
 * doesn't complete (e.g. blocked third-party cookies).
 */
const initKeycloak = (onAuthenticatedCallback) => {
  log("init: starting", {
    path: window.location.pathname,
    search: window.location.search,
    hashLength: window.location.hash.length,
  });

  _kc
    .init({
      onLoad: "check-sso",
      silentCheckSsoRedirectUri:
        window.location.origin + "/silent-check-sso.html",
      pkceMethod: "S256",
      checkLoginIframe: false,
    })
    .then((authenticated) => {
      log("init: finished", { authenticated });
      onAuthenticatedCallback();
    })
    .catch((error) => {
      // Render anyway. RenderOnAuthenticated will route to the login page.
      warn("init: failed", error);
      onAuthenticatedCallback();
    });
};

const doLogin = (...args) => {
  log("login: redirecting to Keycloak");
  return _kc.login(...args);
};

const doRegister = (...args) => {
  log("register: redirecting to Keycloak signup");
  return _kc.register(...args);
};

const doLogout = (...args) => {
  log("logout: redirecting to Keycloak");
  return _kc.logout(...args);
};

const getToken = () => _kc.token;

const isLoggedIn = () => !!_kc.token;

const updateToken = () =>
  _kc.updateToken(5).catch(() => {
    warn("token: update failed, forcing login");
    return doLogin();
  });

// Some realms / scope setups don't include `sub` in the access token but
// it is always present on the id_token (OIDC requirement). Fall back to
// the id_token so callers always get a stable user id.
const getUserId = () => _kc.tokenParsed?.sub ?? _kc.idTokenParsed?.sub;
const getUsername = () => _kc.tokenParsed?.preferred_username;
const getFirstName = () => _kc.tokenParsed?.given_name;
const getLastName = () => _kc.tokenParsed?.family_name;
const getDisplayName = () => _kc.tokenParsed?.name;
const getEMail = () => _kc.tokenParsed?.email;
const getRoleAccessList = () => _kc.getUserRoles?.();

const hasRole = (roles) => roles.some((role) => _kc.hasRealmRole(role));

const UserService = {
  initKeycloak,
  getUserId,
  doLogin,
  doRegister,
  doLogout,
  isLoggedIn,
  getToken,
  updateToken,
  getUsername,
  getFirstName,
  getLastName,
  getDisplayName,
  getEMail,
  getRoleAccessList,
  hasRole,
};

export default UserService;
